import React, { useCallback, useEffect, useRef, useState } from "react";

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

/**
 * Drag-to-crop inside image bounds. When inline=true, UI stays inside the editor field (no fullscreen).
 */
const ImageCropModal = ({ open, imageSrc, onClose, onApplyBlob, inline = false }) => {
  const imgRef = useRef(null);
  const dragRef = useRef(null);
  const [natural, setNatural] = useState({ w: 0, h: 0 });
  const [rect, setRect] = useState(null);
  const [live, setLive] = useState(0);

  useEffect(() => {
    if (!open) {
      setRect(null);
      dragRef.current = null;
    }
  }, [open]);

  const toLocal = useCallback((clientX, clientY) => {
    const el = imgRef.current;
    if (!el) return { x: 0, y: 0 };
    const r = el.getBoundingClientRect();
    const x = clientX - r.left;
    const y = clientY - r.top;
    const w = el.clientWidth;
    const h = el.clientHeight;
    return {
      x: clamp(x, 0, w),
      y: clamp(y, 0, h),
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const onMove = (e) => {
      const d = dragRef.current;
      if (!d) return;
      const p = toLocal(e.clientX, e.clientY);
      d.x = p.x;
      d.y = p.y;
      setLive((x) => x + 1);
    };
    const onUp = () => {
      const d = dragRef.current;
      dragRef.current = null;
      if (!d || !imgRef.current) return;
      const el = imgRef.current;
      const dw = el.clientWidth;
      const dh = el.clientHeight;
      let x1 = Math.min(d.ax, d.x);
      let y1 = Math.min(d.ay, d.y);
      let x2 = Math.max(d.ax, d.x);
      let y2 = Math.max(d.ay, d.y);
      x1 = clamp(x1, 0, dw);
      y1 = clamp(y1, 0, dh);
      x2 = clamp(x2, 0, dw);
      y2 = clamp(y2, 0, dh);
      const rw = x2 - x1;
      const rh = y2 - y1;
      if (rw >= 8 && rh >= 8) {
        setRect({ x: x1, y: y1, w: rw, h: rh });
      }
      setLive((x) => x + 1);
    };
    // Pointer events work for mouse + touch (iOS Safari) + pen
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [open, toLocal]);

  const onImgLoad = (e) => {
    const img = e.target;
    setNatural({ w: img.naturalWidth, h: img.naturalHeight });
  };

  const onPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch (_) {
      /* ignore if capture unsupported */
    }
    const p = toLocal(e.clientX, e.clientY);
    setRect(null);
    dragRef.current = { ax: p.x, ay: p.y, x: p.x, y: p.y };
    setLive((x) => x + 1);
  };

  const applyCrop = useCallback(() => {
    if (!rect || !natural.w || !imgRef.current) return;
    const img = imgRef.current;
    const dw = img.clientWidth;
    const dh = img.clientHeight;
    const sx = (rect.x / dw) * natural.w;
    const sy = (rect.y / dh) * natural.h;
    const sw = (rect.w / dw) * natural.w;
    const sh = (rect.h / dh) * natural.h;

    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(sw));
    canvas.height = Math.max(1, Math.round(sh));
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (blob) onApplyBlob(blob);
        onClose();
      },
      "image/jpeg",
      0.92
    );
  }, [rect, natural, onApplyBlob, onClose]);

  if (!open || !imageSrc) return null;

  const d = dragRef.current;
  const dw = imgRef.current?.clientWidth ?? 0;
  const dh = imgRef.current?.clientHeight ?? 0;

  let overlayDrag = null;
  if (d && dw > 0 && dh > 0) {
    let left = Math.min(d.ax, d.x);
    let top = Math.min(d.ay, d.y);
    let width = Math.abs(d.x - d.ax);
    let height = Math.abs(d.y - d.ay);
    left = clamp(left, 0, dw);
    top = clamp(top, 0, dh);
    width = clamp(width, 0, dw - left);
    height = clamp(height, 0, dh - top);
    if (width > 0 && height > 0) {
      overlayDrag = { left, top, width, height };
    }
  }

  let rectOverlay = null;
  if (rect && !dragRef.current && dw > 0 && dh > 0) {
    const left = clamp(rect.x, 0, dw);
    const top = clamp(rect.y, 0, dh);
    const width = clamp(rect.w, 0, dw - left);
    const height = clamp(rect.h, 0, dh - top);
    if (width > 0 && height > 0) {
      rectOverlay = { left, top, width, height };
    }
  }

  const inner = (
    <>
      <div className="flex-shrink-0 flex items-start justify-between gap-2 px-3 py-2 border-b border-gray-200 bg-gray-50/90">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">Crop image</h3>
          <p className="text-xs text-gray-500 mt-0.5">Drag inside the image. Selection stays within the image.</p>
        </div>
        <button
          type="button"
          className="text-gray-500 hover:text-gray-800 text-lg leading-none px-1"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-3 flex items-center justify-center">
        <div className="w-full max-w-full flex justify-center">
          <div className="relative border border-gray-200 rounded-lg overflow-hidden bg-gray-100 max-w-full shadow-inner">
            <img
              ref={imgRef}
              src={imageSrc}
              alt="Crop"
              className="max-w-full w-auto h-auto max-h-[min(50vh,320px)] sm:max-h-[min(55vh,400px)] block select-none"
              onLoad={onImgLoad}
              draggable={false}
              onPointerDown={onPointerDown}
            />
            {overlayDrag && (
              <div
                className="absolute border-2 border-cyan-500 bg-cyan-200/35 pointer-events-none box-border"
                style={{
                  left: overlayDrag.left,
                  top: overlayDrag.top,
                  width: overlayDrag.width,
                  height: overlayDrag.height,
                }}
              />
            )}
            {rectOverlay && (
              <div
                className="absolute border-2 border-emerald-500 bg-emerald-200/25 pointer-events-none box-border"
                style={{
                  left: rectOverlay.left,
                  top: rectOverlay.top,
                  width: rectOverlay.width,
                  height: rectOverlay.height,
                }}
              />
            )}
          </div>
        </div>
      </div>

      <div className="flex-shrink-0 flex justify-end gap-2 px-3 py-2.5 border-t border-gray-200 bg-gray-50/90">
        <button
          type="button"
          className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-white"
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={!rect}
          className="px-3 py-1.5 text-sm rounded-lg bg-cyan-600 text-white hover:bg-cyan-700 disabled:opacity-40"
          onClick={applyCrop}
        >
          Apply crop
        </button>
      </div>
    </>
  );

  if (inline) {
    return (
      <div
        className="absolute inset-0 z-[60] flex flex-col rounded-b-lg overflow-hidden bg-white/98 backdrop-blur-[2px] border border-cyan-200/80 shadow-[inset_0_1px_0_rgba(255,255,255,0.9)]"
        role="dialog"
        aria-modal="true"
        aria-label="Crop image"
        data-drag-tick={live}
      >
        {inner}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4" data-drag-tick={live}>
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">{inner}</div>
    </div>
  );
};

export default ImageCropModal;
